import { useState, useEffect } from "react";
import { Modal, Button, Row, Col, Card, Spinner, Badge } from "react-bootstrap";
import { format } from "date-fns";
import { formatCurrency } from "../../../../utils/numberFormatter";
import axios from "axios";
import BASE_URL from "../../../../assets/global/url";

const ACCOUNT_HISTORY_BY_ID_URL = (transactionId) =>
  `${BASE_URL}/accountListSub/getAccountHistory/${transactionId}`;

const AccountHistoryView = ({ show, onHide, transactionId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!transactionId || !show) return;

      setLoading(true);
      try {
        const response = await axios.get(
          ACCOUNT_HISTORY_BY_ID_URL(transactionId),
        );
        setData(response.data);
      } catch (error) {
        console.error("Failed to fetch transaction details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [transactionId, show]);

  // UI Helpers
  const isCredit = data?.type === "Credit";
  const typeColor = isCredit ? "danger" : "success";
  const toFromLabel = isCredit ? "To" : "From";
  const toFromName = data?.transacted || "---";
  const currencyName = data?.transacteds?.currency?.currency_name || "PHP";

  const empId = data?.masterlist?.emp_id || "---";
  const transferredByName = data?.masterlist
    ? `${data.masterlist.fname} ${data.masterlist.lname}`
    : "System / Unknown";

  const {
    amountValue = 0,
    balanceAfter = 0,
    balanceBefore = 0,
    liveBalance = 0,
  } = data?.calculated_values || {};

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton className="bg-light border-bottom-0 pb-3">
        <Modal.Title className="text-primary fw-bold fs-5">
          <i className="fa-solid fa-circle-info me-2"></i>
          Account Transaction Information
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4 bg-light pt-0">
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner animation="border" variant="primary" />
            <span className="mt-2 text-muted">Loading details...</span>
          </div>
        ) : !data ? (
          <div className="text-center py-5 text-muted">
            No transaction data found.
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {/* Header / Top Level Info */}
            <div className="d-flex justify-content-between align-items-center bg-white p-3 rounded shadow-sm border">
              <div>
                <h6
                  className="mb-1 text-muted text-uppercase"
                  style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
                >
                  Account Name
                </h6>
                <div className="fw-bold fs-6">
                  {data.transacteds?.account_name || "---"}
                </div>
              </div>
              <div className="text-end">
                <Badge bg={typeColor} className="px-3 py-2 fs-6 rounded-pill">
                  {isCredit ? (
                    <>
                      <i className="fa-solid fa-arrow-up-wide-short"></i>{" "}
                      Withdraw (Credit)
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-arrow-down-short-wide"></i>{" "}
                      Deposit (Debit)
                    </>
                  )}
                </Badge>
              </div>
            </div>

            {/* ROW 1: Transaction Details */}
            <Row className="g-3">
              <Col>
                <Card className="shadow-sm border-0 h-100">
                  <Card.Header className="bg-white border-bottom-0 pt-3 pb-0 d-flex justify-content-between align-items-center">
                    <h6 className="fw-bold text-primary mb-0">
                      <i className="fa-solid fa-file-invoice me-2"></i>{" "}
                      Transaction Details
                    </h6>
                    {data.isTransferOnly && (
                      <Badge bg="warning" text="dark" className="fw-medium">
                        <i className="fa-solid fa-money-bill-transfer me-1"></i>{" "}
                        Transfer Only
                      </Badge>
                    )}
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3">
                      <Col xs={6}>
                        <div className="text-muted small">
                          Transaction Number
                        </div>
                        <div className="fw-medium">
                          {data.transaction_number || "---"}
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted small">Date Processed</div>
                        <div className="fw-medium">
                          {data.date
                            ? format(new Date(data.date), "MMMM dd, yyyy")
                            : "---"}
                        </div>
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={6}>
                        <div className="text-muted small">Module From</div>
                        <div className="fw-medium">
                          {data.module_from || "---"}
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted small">Payment Method</div>
                        <div className="fw-medium">
                          {data.payment_method || "---"}
                        </div>
                      </Col>
                    </Row>

                    <Row>
                      <Col xs={6}>
                        <div className="text-muted small">Check No.</div>
                        <div className="fw-medium">
                          {data.check_or_remarks || "---"}
                        </div>
                      </Col>
                      <Col xs={6}>
                        <div className="text-muted small">{toFromLabel}</div>
                        <div className="fw-medium text-dark">{toFromName}</div>
                      </Col>
                    </Row>

                    <Row className="g-3 mt-1">
                      <Col xs={12}>
                        <Card className="shadow-sm border-0 h-100 bg-white">
                          <Card.Header className="bg-white border-bottom-0 pt-3 pb-0">
                            <h6 className="fw-bold text-primary mb-0">
                              <i className="fa-solid fa-comment-dots me-2"></i>{" "}
                              Remarks
                            </h6>
                          </Card.Header>
                          <Card.Body>
                            <div
                              className="bg-light p-3 rounded border-start border-4 border-primary text-dark"
                              style={{
                                minHeight: "80px",
                                whiteSpace: "pre-wrap",
                              }}
                            >
                              {data.remarks ? (
                                data.remarks
                              ) : (
                                <span className="text-muted fst-italic">
                                  No remarks provided for this transaction.
                                </span>
                              )}
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* ROW 2: Financial Details & User Info */}
            <Row className="g-3">
              {/* Bottom Left: Financial Details */}
              <Col lg={6}>
                <Card className="shadow-sm border-0 h-100 bg-white">
                  <Card.Header className="bg-white border-bottom-0 pt-3 pb-0">
                    <h6 className="fw-bold text-primary mb-0">
                      <i className="fa-solid fa-chart-line me-2"></i> Financial
                      Details
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row className="mb-3 align-items-center bg-light p-2 rounded mx-0">
                      <Col xs={6}>
                        <div className="text-muted small">Currency Rate</div>
                        <div className="fw-bold">{data.rate || "1.00"}</div>
                      </Col>
                      <Col xs={6} className="text-end">
                        <div className="text-muted small">Amount</div>
                        <div className={`fw-bold fs-5 text-${typeColor}`}>
                          {isCredit ? "- " : "+ "}
                          {formatCurrency(amountValue, currencyName)}
                        </div>
                      </Col>
                    </Row>

                    {/* Balance Breakdown Timeline */}
                    <div className="px-2">
                      {data.transacteds?.investment_amount > 0 && (
                        <div className="d-flex justify-content-between mb-2">
                          <span className="text-muted small">
                            Initial Investment Amount
                          </span>
                          <span className="fw-medium text-dark opacity-75">
                            {formatCurrency(
                              data.transacteds.investment_amount,
                              currencyName,
                            )}
                          </span>
                        </div>
                      )}

                      <div className="d-flex justify-content-between mb-2">
                        <span className="text-muted small">
                          Balance (Before Transaction)
                        </span>
                        <span className="fw-medium text-dark opacity-75">
                          {formatCurrency(balanceBefore, currencyName)}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between mb-3">
                        <span className="text-muted small">
                          Balance (After Transaction)
                        </span>
                        <span className="fw-bold text-dark">
                          {formatCurrency(balanceAfter, currencyName)}
                        </span>
                      </div>

                      <hr className="text-muted opacity-25" />

                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small fw-bold">
                          Live Account Balance (Current)
                        </span>
                        <span className="fw-bold text-primary fs-5">
                          {formatCurrency(liveBalance, currencyName)}
                        </span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>

              {/* Bottom Right: User Info */}
              <Col lg={6}>
                <Card className="shadow-sm border-0 h-100 bg-white">
                  <Card.Header className="bg-white border-bottom-0 pt-3 pb-0">
                    <h6 className="fw-bold text-primary mb-0">
                      <i className="fa-solid fa-user-shield me-2"></i> User
                      Information
                    </h6>
                  </Card.Header>
                  <Card.Body className="d-flex flex-column justify-content-center align-items-center text-center">
                    <div
                      className="bg-light text-primary rounded-circle d-flex justify-content-center align-items-center mb-3"
                      style={{
                        width: "60px",
                        height: "60px",
                        fontSize: "1.5rem",
                      }}
                    >
                      <i className="fa-solid fa-user-tie"></i>
                    </div>

                    <div className="w-100 px-3">
                      <div className="text-muted small mb-1">Transacted By</div>
                      <div className="fw-bold fs-6 text-dark mb-3">
                        {transferredByName}
                      </div>

                      <div className="bg-light rounded p-2">
                        <div className="text-muted small mb-1">Employee ID</div>
                        <div className="fw-medium font-monospace">{empId}</div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light border-top-0 d-flex justify-content-end">
        <Button variant="secondary" onClick={onHide} className="px-4">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export { AccountHistoryView };
